import { ApiService } from '../../api/api-service';
import {
  ASCE_STANDARDS_VERSION,
  AsceLoadResult,
  AsceSnowResponse,
  AsceWindResponse,
  MRI_KEY_BY_RISK_LEVEL,
} from '../../types';
import { logDebug } from '../../utils';

const SERVICE_ROOT = '/asce';

/**
 * Service for ASCE 7 Hazard API calls (proxied through backend).
 * All calls go through the backend to protect the ASCE API token.
 */
export class AsceService extends ApiService {
  async getWindLoad(lat: number, lon: number, riskLevel: number = 1): Promise<AsceWindResponse> {
    return this.handleApiCall(
      () =>
        this.client.get<AsceWindResponse>(`${SERVICE_ROOT}/wind`, {
          params: {
            lat,
            lon,
            standardsVersion: ASCE_STANDARDS_VERSION,
            riskLevel,
          },
        }),
      'Error fetching ASCE wind load data'
    );
  }

  async getSnowLoad(lat: number, lon: number, riskLevel: number = 1): Promise<AsceSnowResponse> {
    return this.handleApiCall(
      () =>
        this.client.get<AsceSnowResponse>(`${SERVICE_ROOT}/snow`, {
          params: {
            lat,
            lon,
            standardsVersion: ASCE_STANDARDS_VERSION,
            riskLevel,
          },
        }),
      'Error fetching ASCE snow load data'
    );
  }

  /**
   * Extracts the ground snow load (psf) from the ASCE response, handling
   * both the 7-10/7-16 GIS features format and the 7-22 flat key-value format.
   */
  private extractSnowLoad(snowResponse: AsceSnowResponse, riskLevel: number): number {
    const results = snowResponse.snow.snowResults;

    // --- 7-10 / 7-16: features-based format ---
    if (Array.isArray(results) && results.length > 0 && results[0].features?.length) {
      const attrs = results[0].features[0].attributes;
      // Prefer lb_ft2 (direct psf), then Display_1 parsed, then LoadXX_1 for elevation ranges
      if (attrs.lb_ft2 != null && attrs.lb_ft2 > 0) return Math.round(attrs.lb_ft2);
      const display = parseFloat(String(attrs.Display_1));
      if (!isNaN(display) && display > 0) return Math.round(display);
      if (attrs.Load1_1 != null && attrs.Load1_1 > 0) return Math.round(attrs.Load1_1);
    }

    // --- 7-22: flat key-value array ---
    if (Array.isArray(results) && results.length > 0 && results[0].key != null) {
      const riskKey = `riskCategory${riskLevel}`;
      const entries = results as Array<{ key: string; value: string | number | null }>;

      const riskEntry = entries.find(e => e.key === riskKey);
      if (riskEntry?.value != null) {
        const v = parseFloat(String(riskEntry.value));
        if (!isNaN(v)) return Math.round(v);
      }

      const mri = entries.find(e => e.key === 'mri20yr');
      if (mri?.value != null) {
        const v = parseFloat(String(mri.value));
        if (!isNaN(v)) return Math.round(v);
      }
    }

    // --- 7-22: object format (snowResults as Record) ---
    if (results && !Array.isArray(results)) {
      const obj = results as Record<string, string | number | null>;
      const riskKey = `riskCategory${riskLevel}`;
      if (obj[riskKey] != null) {
        const v = parseFloat(String(obj[riskKey]));
        if (!isNaN(v)) return Math.round(v);
      }
      if (obj.mri20yr != null) {
        const v = parseFloat(String(obj.mri20yr));
        if (!isNaN(v)) return Math.round(v);
      }
      // Alaska sub-format
      if (obj.attributes && typeof obj.attributes === 'object') {
        const attrs = obj.attributes as unknown as Record<string, string | number | null>;
        const valueKey = `Value${riskLevel}`;
        if (attrs[valueKey] != null) {
          const v = parseFloat(String(attrs[valueKey]));
          if (!isNaN(v)) return Math.round(v);
        }
      }
    }

    logDebug('ASCE snow response could not be parsed — returning 0', snowResponse.snow);
    return 0;
  }

  /**
   * Fetches both wind and snow load data in parallel and extracts the
   * display values based on risk level.
   */
  async getLoadRatings(lat: number, lon: number, riskLevel: number = 1): Promise<AsceLoadResult> {
    const [windResponse, snowResponse] = await Promise.all([
      this.getWindLoad(lat, lon, riskLevel),
      this.getSnowLoad(lat, lon, riskLevel),
    ]);

    const mriKey = MRI_KEY_BY_RISK_LEVEL[riskLevel] ?? '300';
    const windSpeedStr = windResponse.wind.mriWindResults?.[mriKey];
    const windLoadMph = windSpeedStr != null ? parseInt(String(windSpeedStr), 10) : NaN;

    // The ASCE API can return a 200 response with missing or non-numeric wind
    // values for locations it cannot evaluate (e.g. coordinates outside the US,
    // such as reversed lat/lon). parseInt then yields NaN, which must never be
    // surfaced to the user. Treat any non-positive/non-finite value as "no data".
    if (!Number.isFinite(windLoadMph) || windLoadMph <= 0) {
      throw new Error(
        'Load rating data is not available for this location. Please verify the address/coordinates are within the United States.'
      );
    }

    let snowLoadPsf = 0;
    let snowParseWarning = false;
    if (!snowResponse.snow.noSnowFeatures) {
      snowLoadPsf = this.extractSnowLoad(snowResponse, riskLevel);
      if (snowLoadPsf === 0) {
        snowParseWarning = true;
      }
    }

    return {
      windLoadMph,
      snowLoadPsf,
      snowParseWarning,
      elevation: windResponse.requestInfo.siteInfo.elevation,
      isHurricaneWindDebrisZone: windResponse.wind.isHurricaneWindDebrisZone,
      isSpecialWindZone: windResponse.wind.isSpecialWindZone,
      isNewHampshire_7_16: windResponse.wind.isNewHampshire_7_16 ?? false,
      mriWindResults: windResponse.wind.mriWindResults,
      riskLevel,
      rawWindResponse: windResponse,
      rawSnowResponse: snowResponse,
    };
  }
}
